import { type ReactNode } from 'react';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Button, buttonClass } from './Button.tsx';

/**
 * 화면 안 다이얼로그 (브라우저 alert/confirm/prompt 대체). 프로미스 기반 명령형 API.
 *   await dialog.alert('메시지'); if (await dialog.confirm('진행할까요?', { danger: true })) …; const v = await dialog.prompt('사유', { multiline: true });
 * DialogHost 는 main.tsx 에 한 번만 둔다(분할 화면의 별도 루트에서도 같은 스토어를 쓰므로 하나만 렌더).
 */
type Kind = 'alert' | 'confirm' | 'prompt';
export interface DialogOptions { title?: string; okText?: string; cancelText?: string; danger?: boolean; defaultValue?: string; multiline?: boolean; placeholder?: string }
interface Req { id: number; kind: Kind; message: ReactNode; opts: DialogOptions; resolve: (v: unknown) => void }
let queue: Req[] = []; let seq = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
function push(kind: Kind, message: ReactNode, opts: DialogOptions = {}) {
  return new Promise<unknown>((resolve) => { queue = [...queue, { id: ++seq, kind, message, opts, resolve }]; emit(); });
}
function settle(id: number, value: unknown) { const r = queue.find((q) => q.id === id); queue = queue.filter((q) => q.id !== id); emit(); r?.resolve(value); }
export const dialog = {
  alert: (message: ReactNode, opts?: DialogOptions) => push('alert', message, opts) as Promise<void>,
  confirm: (message: ReactNode, opts?: DialogOptions) => push('confirm', message, opts) as Promise<boolean>,
  prompt: (message: ReactNode, opts?: DialogOptions) => push('prompt', message, opts) as Promise<string | null>,
};

export function DialogHost() {
  const cur = useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => queue[0] ?? null, () => null);
  if (!cur) return null;
  return <DialogCard key={cur.id} req={cur} />;
}
function DialogCard({ req }: { req: Req }) {
  const [value, setValue] = useState(req.opts.defaultValue ?? '');
  const okRef = useRef<HTMLButtonElement>(null); const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const ok = () => settle(req.id, req.kind === 'alert' ? undefined : req.kind === 'confirm' ? true : value);
  const cancel = () => settle(req.id, req.kind === 'alert' ? undefined : req.kind === 'confirm' ? false : null);
  useEffect(() => { (req.kind === 'prompt' ? inputRef.current : okRef.current)?.focus(); }, [req.kind]);
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); cancel(); } else if (e.key === 'Enter' && !(req.kind === 'prompt' && req.opts.multiline)) { e.preventDefault(); ok(); } };
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h);
  }); // eslint-disable-line
  const title = req.opts.title ?? (req.kind === 'alert' ? '알림' : req.kind === 'confirm' ? '확인' : '입력');
  return (
    <div className="fixed inset-0 z-[1000] flex items-start justify-center bg-slate-900/40 pt-[18vh]" onMouseDown={(e) => { if (e.target === e.currentTarget) cancel(); }} role="dialog" aria-modal="true" aria-label={title}>
      <div className="w-[440px] max-w-[calc(100vw-2rem)] bg-white border border-slate-300 shadow-[0_16px_48px_-12px_rgba(2,6,23,0.35)]">
        <div className={`px-4 py-2 border-b text-[13px] font-semibold ${req.opts.danger ? 'bg-red-50 border-red-200 text-red-800' : 'bg-slate-50 border-slate-200 text-slate-800'}`}>{title}</div>
        <div className="px-4 py-3 text-[13px] text-slate-800 whitespace-pre-wrap leading-relaxed">{req.message}
          {req.kind === 'prompt' && (req.opts.multiline
            ? <textarea ref={inputRef as React.RefObject<HTMLTextAreaElement>} className="ft-control mt-2 w-full font-mono text-[12px]" rows={5} value={value} placeholder={req.opts.placeholder} onChange={(e) => setValue(e.target.value)} />
            : <input ref={inputRef as React.RefObject<HTMLInputElement>} className="ft-control mt-2 w-full" value={value} placeholder={req.opts.placeholder} onChange={(e) => setValue(e.target.value)} />)}
        </div>
        <div className="px-4 py-2 border-t border-slate-200 flex justify-end gap-2 bg-slate-50">
          {req.kind !== 'alert' && <Button variant="secondary" onClick={cancel}>{req.opts.cancelText ?? '취소'}</Button>}
          <button ref={okRef} type="button" className={buttonClass(req.opts.danger ? 'danger' : 'primary')} onClick={ok}>{req.opts.okText ?? '확인'}</button>
        </div>
      </div>
    </div>
  );
}
if (import.meta.env.DEV) (window as unknown as { prismDialog?: typeof dialog }).prismDialog = dialog;
